import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Users, Loader2, X, Search } from "lucide-react";
import { turmasAPI } from "../../../services/turmas";
import { GrupoTurma } from "../../../types/turmas";
import { api } from "../../../services/api";

interface MilitarGrupo {
  id: string;
  id_militar: string;
  militar: {
    id: string;
    nick: string;
    patente: {
      patente: string;
      abreviacao: string;
    };
  };
}

interface GerenciarMembrosModalProps {
  isOpen: boolean;
  onClose: () => void;
  idGrupo: string;
  turma: GrupoTurma;
  onSuccess?: () => void;
}

export function GerenciarMembrosModal({
  isOpen,
  onClose,
  idGrupo,
  turma,
  onSuccess
}: GerenciarMembrosModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadingMembros, setLoadingMembros] = useState(true);
  const [todosMembros, setTodosMembros] = useState<MilitarGrupo[]>([]);
  const [membrosAtuais, setMembrosAtuais] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      loadMembros();
    }
  }, [isOpen, turma.id]);

  const loadMembros = async () => {
    try {
      setLoadingMembros(true);

      // Carregar todos os membros do grupo
      const response = await api.get(`/militares-grupos/grupo/${idGrupo}`);
      setTodosMembros(response.data);

      // Marcar membros que já estão na turma
      const membrosIds = new Set(turma.membros?.map(m => m.id_militar_grupo) || []);
      setMembrosAtuais(membrosIds);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar membros", {
        variant: "error",
      });
    } finally {
      setLoadingMembros(false);
    }
  };

  const handleToggleMembro = (idMilitarGrupo: string) => {
    const newMembros = new Set(membrosAtuais);
    if (newMembros.has(idMilitarGrupo)) {
      newMembros.delete(idMilitarGrupo);
    } else {
      newMembros.add(idMilitarGrupo);
    }
    setMembrosAtuais(newMembros);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const membrosOriginais = new Set(turma.membros?.map(m => m.id_militar_grupo) || []);

      // Identificar membros a adicionar e remover
      const paraAdicionar = Array.from(membrosAtuais).filter(id => !membrosOriginais.has(id));
      const paraRemover = Array.from(membrosOriginais).filter(id => !membrosAtuais.has(id));

      // Adicionar novos membros
      if (paraAdicionar.length > 0) {
        await turmasAPI.adicionarMembros(turma.id, paraAdicionar);
      }

      // Remover membros
      if (paraRemover.length > 0) {
        await turmasAPI.removerMembros(turma.id, paraRemover);
      }

      enqueueSnackbar("Membros atualizados com sucesso!", { variant: "success" });
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar membros", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  const filteredMembros = todosMembros.filter(mg =>
    mg.militar.nick.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mg.militar.patente.patente.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Gerenciar Membros - {turma.nome}
            </CardTitle>
            <CardDescription>
              Selecione os membros que farão parte desta turma
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden flex flex-col">
          {/* Busca */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome ou patente..."
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              disabled={loading || loadingMembros}
            />
          </div>

          {/* Lista de membros */}
          <div className="flex-1 overflow-y-auto border border-border rounded-md p-4 space-y-2">
            {loadingMembros ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[oklch(0.45_0.15_142)]" />
                <span className="ml-2 text-muted-foreground">Carregando membros...</span>
              </div>
            ) : filteredMembros.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? "Nenhum membro encontrado" : "Nenhum membro disponível"}
              </div>
            ) : (
              filteredMembros.map((mg) => (
                <div
                  key={mg.id}
                  className="flex items-center gap-3 p-3 rounded-md hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => !loading && handleToggleMembro(mg.id)}
                >
                  <input
                    type="checkbox"
                    checked={membrosAtuais.has(mg.id)}
                    onChange={() => handleToggleMembro(mg.id)}
                    className="w-4 h-4 text-[oklch(0.45_0.15_142)] bg-background border-border rounded focus:ring-ring focus:ring-2"
                    disabled={loading}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-foreground">
                      {mg.militar.nick}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {mg.militar.patente.abreviacao || mg.militar.patente.patente}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Resumo */}
          <div className="mt-4 p-3 bg-accent rounded-md text-sm">
            <strong>{membrosAtuais.size}</strong> {membrosAtuais.size === 1 ? 'membro selecionado' : 'membros selecionados'}
          </div>

          {/* Botões */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || loadingMembros}
              className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar Alterações"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
